import { TextBase } from '@carboncopyco/ui';
import { RadioGroup } from '@headlessui/react';
import classNames from 'classnames';
import type React from 'react';
import { forwardRef } from 'react';
import { SearchInput } from '../../UrlStateControls';
import { TitleCard, type TitleCardProps } from './TitleCard';

export type Tab<T extends string> = {
  id: T;
  label: string;
};

type CollectionCardProps<TabId extends string> = {
  children: React.ReactElement;
} & (
  | { withSearch?: boolean; inputPlaceholder: string }
  | { withSearch?: never; inputPlaceholder?: never }
) &
  (
    | {
        header?: never;
        currentTabId: TabId;
        tabs: Tab<TabId>[];
        setTab: (tabId: TabId) => void;
      }
    | {
        header: React.ReactElement;
        currentTabId?: never;
        tabs?: never;
        setTab?: never;
      }
  ) &
  Omit<TitleCardProps, 'header'>;

export const CollectionCardInner = <TabId extends string>(
  {
    header,
    currentTabId,
    setTab,
    tabs,
    children,
    shadow,
    border = 'ds-regular',
    withSearch,
    inputPlaceholder,
  }: CollectionCardProps<TabId>,
  ref: React.ForwardedRef<{ resetSearchInput: () => void }>
) => (
  <TitleCard
    shadow={shadow}
    border={border}
    header={
      <div className="flex justify-between items-center pb-0 px-6">
        {tabs ? (
          <RadioGroup
            value={currentTabId}
            onChange={setTab}
          >
            <div
              className="grid gap-2"
              style={{
                gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`,
              }}
            >
              {tabs.map(({ id, label }) => {
                return (
                  <RadioGroup.Option
                    key={id}
                    value={id}
                    className="self-end"
                  >
                    {({ checked }) => (
                      <div
                        className={classNames(
                          'text-ds-primary-300 flex justify-center hover:cursor-pointer px-9 pt-5 pb-5 border-b-3 border-transparent',
                          {
                            '!border-ds-primary': checked,
                          }
                        )}
                      >
                        <TextBase
                          label={label}
                          color={
                            checked
                              ? 'text-ds-primary-300'
                              : 'text-ds-natural-600'
                          }
                        />
                      </div>
                    )}
                  </RadioGroup.Option>
                );
              })}
            </div>
          </RadioGroup>
        ) : (
          header
        )}
        {withSearch && (
          <div className="w-[320px]">
            <SearchInput
              ref={ref}
              placeholder={inputPlaceholder}
            />
          </div>
        )}
      </div>
    }
  >
    {children}
  </TitleCard>
);

export const CollectionCard = forwardRef(CollectionCardInner) as <
  T extends string,
>(
  props: CollectionCardProps<T> & {
    ref?: React.ForwardedRef<{
      resetSearchInput: () => void;
    }>;
  }
) => ReturnType<typeof CollectionCardInner>;
